import jsPDF from "jspdf";

// PDF Generator using template_settings and quote_data
export async function generateProposalPDF(templateSettings, quoteData) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const leftMargin = templateSettings?.layout?.pageMargins?.left || 20;
  const rightMargin = templateSettings?.layout?.pageMargins?.right || 20;
  const contentWidth = pageWidth - leftMargin - rightMargin;
  
  // Font mapping
  const fontMap = {
    garamond: "times",
    times: "times",
    helvetica: "helvetica",
    courier: "courier"
  };
  const defaultFont = fontMap[templateSettings?.layout?.defaultFontFamily] || "times";
  
  // Calculate footer height (for logo) - reserve space at bottom
  const footerConfig = templateSettings?.footer || {};
  const logoConfig = footerConfig?.logo || {};
  // Only use logo if URL is present, not empty, and visible is not explicitly false
  const logoUrl = logoConfig?.url && logoConfig.url.trim() !== '' && logoConfig?.visible !== false ? logoConfig.url : null;
  const logoHeight = logoConfig?.height || 30;
  const footerMargin = logoUrl ? logoHeight + 20 : 20; // Extra space if logo present
  
  let y = templateSettings?.layout?.pageMargins?.top || 20;
  
  // Helper: Convert hex to RGB array (safe)
  function hexToRgb(hex) {
    if (!hex || typeof hex !== 'string') return [0, 0, 0];
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
    }
    return [0, 0, 0];
  }
  
  // Helper: Set text color from hex
  function setTextColorHex(hexColor) {
    const rgb = hexToRgb(hexColor || "#000000");
    doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  }
  
  // Helper: Set draw color from hex
  function setDrawColorHex(hexColor) {
    const rgb = hexToRgb(hexColor || "#000000");
    doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  }
  
  // Helper: Set font style (with safe defaults)
  const setFontStyle = (style = {}) => {
    doc.setFontSize(style.fontSize || 10);
    doc.setFont(defaultFont, style.fontWeight === "bold" ? "bold" : "normal");
    setTextColorHex(style.fontColor || "#000000");
  };
  
  // Helper: Get text X position based on alignment
  const getTextX = (alignment) => {
    if (alignment === "center") return pageWidth / 2;
    if (alignment === "right") return pageWidth - 20;
    return 20; // left
  };
  
  // Helper: Get text align option
  const getAlignOption = (alignment) => {
    if (alignment === "center") return { align: "center" };
    if (alignment === "right") return { align: "right" };
    return {};
  };
  
  // Helper: Render text with word-wrap support
  const renderWrappedText = (text, x, startY, maxWidth, style = {}, alignment = "left") => {
    if (!text) return startY;
    
    setFontStyle(style);
    const fontSize = style.fontSize || 10;
    const lineHeight = fontSize * 0.4; // Approximate line height in mm
    
    // Split text into lines that fit within maxWidth
    const lines = doc.splitTextToSize(text, maxWidth);
    
    let currentY = startY;
    for (const line of lines) {
      checkPageBreak(lineHeight + 2);
      doc.text(line, x, currentY, getAlignOption(alignment));
      currentY += lineHeight + 2;
    }
    
    return currentY;
  };
  
  // Helper: Check page break - accounts for footer/logo space
  const checkPageBreak = (neededSpace = 20) => {
    if (y + neededSpace > pageHeight - footerMargin) {
      doc.addPage();
      y = 20;
      return true;
    }
    return false;
  };
  
  // Helper: Replace placeholders in text
  const replacePlaceholders = (text, data) => {
    if (!text) return "";
    return text
      .replace(/\{\{guest_name\}\}/g, data.guestName || "")
      .replace(/\{\{today_date\}\}/g, new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }))
      .replace(/\{\{check_in\}\}/g, data.checkIn || "")
      .replace(/\{\{check_out\}\}/g, data.checkOut || "")
      .replace(/\{\{nights\}\}/g, data.nights || "")
      .replace(/\{\{adults\}\}/g, data.adults || "")
      .replace(/\{\{children_text\}\}/g, data.children > 0 ? `, ${data.children} children` : "")
      .replace(/\{\{infants_text\}\}/g, data.infants > 0 ? `, ${data.infants} infants` : "");
  };
  
  // ============================================
  // RENDER HEADER TOP RIGHT (first, at top-right corner)
  // ============================================
  const headerRight = templateSettings?.headerTopRight;
  let headerTopRightHeight = 0;
  
  if (headerRight?.visible !== false) {
    const startY = y;
    
    // Save current position - we render at top right
    let rightY = y;
    
    // Prepared line with guest_name placeholder support
    if (headerRight?.preparedLine?.text) {
      setFontStyle(headerRight?.preparedLine || {});
      
      // Replace {{guest_name}} placeholder with actual name
      let preparedText = headerRight.preparedLine.text;
      preparedText = preparedText.replace(/\{\{guest_name\}\}/g, quoteData?.guestName || "");
      
      // Word-wrap support for long names
      const preparedLines = doc.splitTextToSize(preparedText, contentWidth * 0.6);
      preparedLines.forEach(line => {
        doc.text(line.trim(), pageWidth - rightMargin, rightY, { align: "right" });
        rightY += 4;
      });
      rightY += (headerRight.preparedLine?.marginBottom || 0);
    }
    
    // Location/Date line
    if (headerRight?.locationDateLine?.text) {
      setFontStyle(headerRight.locationDateLine);
      const locationText = replacePlaceholders(headerRight.locationDateLine.text, quoteData);
      doc.text(locationText, pageWidth - rightMargin, rightY, { align: "right" });
      rightY += (headerRight?.locationDateLine?.marginBottom || 0) + 4;
    }
    
    // Calculate total height used by header top right
    headerTopRightHeight = rightY - startY + (headerRight?.blockMarginBottom || 10);
    
    // Move y below the header top right block
    y = startY + headerTopRightHeight;
  }
  
  // ============================================
  // RENDER BRAND HEADER (centered, BELOW header top right)
  // ============================================
  const brand = templateSettings?.brandHeader;
  if (brand?.visible !== false) {
    // Add extra spacing to ensure brand header is below top-right header
    y += 5;
    
    // Title - WITH WORD WRAP
    if (brand?.title?.text) {
      setFontStyle(brand.title);
      const titleLines = doc.splitTextToSize(brand.title.text, contentWidth);
      titleLines.forEach(line => {
        doc.text(line, pageWidth / 2, y, { align: "center" });
        y += 6;
      });
      y += (brand.title.marginBottom || 2);
    }
    
    // Subtitle - WITH WORD WRAP
    if (brand?.subtitle?.text) {
      setFontStyle(brand.subtitle);
      const subtitleLines = doc.splitTextToSize(brand.subtitle.text, contentWidth);
      subtitleLines.forEach(line => {
        doc.text(line, pageWidth / 2, y, { align: "center" });
        y += 5;
      });
      y += (brand.subtitle.marginBottom || 10);
    }
  }
  
  // ============================================
  // RENDER DOCUMENT TITLE (centered)
  // ============================================
  const docTitle = templateSettings?.documentTitle;
  if (docTitle?.visible !== false && docTitle?.text) {
    setFontStyle(docTitle);
    const docTitleLines = doc.splitTextToSize(docTitle.text, contentWidth);
    docTitleLines.forEach(line => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += 6;
    });
    y += (docTitle.marginBottom || 10);
  }
  
  // ============================================
  // RENDER INTRO SECTION
  // ============================================
  const intro = templateSettings?.intro;
  if (intro?.visible !== false) {
    // Offer valid - WITH WORD WRAP
    if (intro?.offerValid?.visible !== false && intro?.offerValid?.text) {
      setFontStyle(intro.offerValid);
      const offerLines = doc.splitTextToSize(intro.offerValid.text, contentWidth);
      offerLines.forEach(line => {
        doc.text(line, getTextX(intro.offerValid.alignment), y, getAlignOption(intro.offerValid.alignment));
        y += 5;
      });
      y += (intro.offerValid.marginBottom || 3);
    }
    
    // Body text - WITH WORD WRAP
    if (intro?.bodyText?.visible !== false && intro?.bodyText?.text) {
      setFontStyle(intro.bodyText);
      const lines = doc.splitTextToSize(intro.bodyText.text, contentWidth);
      lines.forEach(line => {
        doc.text(line, getTextX(intro.bodyText.alignment), y, getAlignOption(intro.bodyText.alignment));
        y += 5;
      });
      y += (intro.bodyText.marginBottom || 10);
    }
  }
  
  // ============================================
  // RENDER STAY DETAILS
  // ============================================
  const stayDetails = templateSettings?.stayDetails;
  if (stayDetails?.visible !== false) {
    checkPageBreak(50);
    
    // Get alignment for stay details (default: left)
    const stayAlignment = stayDetails?.alignment || 'left';
    const getStayX = () => {
      if (stayAlignment === 'center') return pageWidth / 2;
      if (stayAlignment === 'right') return pageWidth - rightMargin;
      return leftMargin;
    };
    const stayAlignOption = stayAlignment === 'center' ? { align: 'center' } : 
                           stayAlignment === 'right' ? { align: 'right' } : {};
    
    // Section title
    if (stayDetails?.sectionTitle?.text) {
      setFontStyle(stayDetails.sectionTitle);
      const titleAlign = stayDetails.sectionTitle.alignment || stayAlignment;
      const titleX = titleAlign === 'center' ? pageWidth / 2 : 
                     titleAlign === 'right' ? pageWidth - rightMargin : leftMargin;
      doc.text(stayDetails.sectionTitle.text, titleX, y, titleAlign === 'center' ? { align: 'center' } : 
               titleAlign === 'right' ? { align: 'right' } : {});
      y += (stayDetails.sectionTitle.marginBottom || 8);
    }
    
    // Check-in/out line
    if (stayDetails?.checkInOutLine) {
      setFontStyle(stayDetails.checkInOutLine);
      const checkInText = `Check-in: ${quoteData?.checkIn || ''} | Check-out: ${quoteData?.checkOut || ''} | ${quoteData?.nights || 0} nights`;
      const checkInLines = doc.splitTextToSize(checkInText, contentWidth);
      checkInLines.forEach(line => {
        doc.text(line, getStayX(), y, stayAlignOption);
        y += 4;
      });
      y += (stayDetails.checkInOutLine.marginBottom || 2);
    }
    
    // Guests line
    if (stayDetails?.guestsLine) {
      setFontStyle(stayDetails.guestsLine);
      let guestsText = `Guests: ${quoteData?.adults || 0} adults`;
      if (quoteData?.children > 0) guestsText += `, ${quoteData.children} children`;
      if (quoteData?.infants > 0) guestsText += `, ${quoteData.infants} infants`;
      
      const guestsLines = doc.splitTextToSize(guestsText, contentWidth);
      guestsLines.forEach(line => {
        doc.text(line, getStayX(), y, stayAlignOption);
        y += 4;
      });
      y += (stayDetails.guestsLine.marginBottom || 6);
    }
    
    // Nightly rows
    if (quoteData?.nightlyRates && quoteData.nightlyRates.length > 0) {
      setFontStyle(stayDetails?.nightlyRows || {});
      
      quoteData.nightlyRates.forEach((night) => {
        checkPageBreak(12);
        
        const date = new Date(night.date);
        const dateStr = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        
        // Build row with extra guests
        let rowText = `${dateStr} — Night rate €${night.rate}`;
        
        // Add extra guest labels
        if (quoteData?.extraGuestLabels && quoteData.extraGuestLabels.length > 0) {
          quoteData.extraGuestLabels.forEach(label => {
            rowText += ` + ${label}`;
          });
        }
        
        // Add infants
        for (let i = 0; i < (quoteData?.infants || 0); i++) {
          rowText += ` + Infant (0Y) €0`;
        }
        
        rowText += ` = €${night.total || night.rate}`;
        
        // Use word-wrap for long rows
        const rowLines = doc.splitTextToSize(rowText, contentWidth);
        rowLines.forEach(line => {
          doc.text(line, getStayX(), y, stayAlignOption);
          y += 4;
        });
        y += (stayDetails?.nightlyRows?.marginBottom || 1);
      });
    }
    
    y += 3;
    
    // Cleaning fee
    if (stayDetails?.cleaningFee && (quoteData?.cleaningFee || 0) > 0) {
      setFontStyle(stayDetails.cleaningFee);
      const cleaningText = `Cleaning fee (one-time): €${quoteData.cleaningFee.toFixed(2)}`;
      const cleaningLines = doc.splitTextToSize(cleaningText, contentWidth);
      cleaningLines.forEach(line => {
        doc.text(line, getStayX(), y, stayAlignOption);
        y += 4;
      });
      y += (stayDetails.cleaningFee.marginBottom || 1);
    }
    
    // City tax
    if (stayDetails?.cityTax && (quoteData?.cityTaxTotal || 0) > 0) {
      setFontStyle(stayDetails.cityTax);
      const cityTaxText = `City tax (${quoteData?.taxablePeople || 0} persons × €${quoteData?.cityTaxRate || 0} × ${quoteData?.taxableNights || 0} nights): €${quoteData.cityTaxTotal.toFixed(2)}`;
      const cityTaxLines = doc.splitTextToSize(cityTaxText, contentWidth);
      cityTaxLines.forEach(line => {
        doc.text(line, getStayX(), y, stayAlignOption);
        y += 4;
      });
      y += (stayDetails.cityTax.marginBottom || 10);
    }
  }
  
  // ============================================
  // RENDER PRICE BREAKDOWN
  // ============================================
  const breakdown = templateSettings?.priceBreakdown;
  if (breakdown?.visible !== false) {
    checkPageBreak(60);
    
    // Section title - supports alignment (can be centered)
    if (breakdown?.sectionTitle?.text) {
      setFontStyle(breakdown.sectionTitle);
      const titleAlign = breakdown.sectionTitle.alignment || 'left';
      const titleX = titleAlign === 'center' ? pageWidth / 2 : 
                     titleAlign === 'right' ? pageWidth - rightMargin : leftMargin;
      const titleAlignOption = titleAlign === 'center' ? { align: 'center' } : 
                               titleAlign === 'right' ? { align: 'right' } : {};
      doc.text(breakdown.sectionTitle.text, titleX, y, titleAlignOption);
      y += (breakdown.sectionTitle.marginBottom || 10);
    }
    
    const rowStyle = breakdown?.rows || {};
    const labelWidth = contentWidth * 0.7; // 70% for label, 30% for value
    
    // Helper function for two-column row with word-wrap (always left-aligned for data rows)
    const renderPriceRow = (label, value, labelStyle = {}, valueStyle = {}) => {
      // Set label style
      doc.setFontSize(labelStyle.fontSize || rowStyle.labelFontSize || 10);
      doc.setFont(defaultFont, labelStyle.fontWeight === "bold" || rowStyle.labelFontWeight === "bold" ? "bold" : "normal");
      setTextColorHex(labelStyle.fontColor || rowStyle.labelFontColor || "#000000");
      
      // Split label text for word-wrap
      const labelLines = doc.splitTextToSize(label, labelWidth);
      const startY = y;
      
      labelLines.forEach((line, idx) => {
        doc.text(line, leftMargin, y);
        if (idx < labelLines.length - 1) y += 4;
      });
      
      // Set value style and render on same line as last label line
      doc.setFontSize(valueStyle.fontSize || rowStyle.valueFontSize || 10);
      doc.setFont(defaultFont, valueStyle.fontWeight === "bold" || rowStyle.valueFontWeight === "bold" ? "bold" : "normal");
      setTextColorHex(valueStyle.fontColor || rowStyle.valueFontColor || "#000000");
      doc.text(value, pageWidth - rightMargin, y, { align: "right" });
      
      y += (rowStyle.marginBottom || 4) + 2;
    };
    
    // Accommodation
    const nights = quoteData?.nights || 1;
    const accommodationTotal = quoteData?.accommodationTotal || 0;
    renderPriceRow(
      `Accommodation (${nights} nights × €${(accommodationTotal / nights).toFixed(2)} avg)`,
      `€${accommodationTotal.toFixed(2)}`
    );
    
    // Extra guest fee
    if ((quoteData?.extraGuestFeeTotal || 0) > 0) {
      checkPageBreak(8);
      renderPriceRow(
        `Extra Guest Fee (${quoteData.extraGuestCount} guest${quoteData.extraGuestCount > 1 ? 's' : ''} × €${quoteData.extraGuestFeePerNight} × ${nights} nights)`,
        `€${quoteData.extraGuestFeeTotal.toFixed(2)}`
      );
    }
    
    // Cleaning fee
    checkPageBreak(8);
    renderPriceRow("Cleaning Fee", `€${(quoteData?.cleaningFee || 0).toFixed(2)}`);
    
    // Special Offer (was Discount)
    if ((quoteData?.discount || 0) > 0) {
      checkPageBreak(8);
      renderPriceRow("Special Offer", `-€${quoteData.discount.toFixed(2)}`);
    }
    
    // Divider before subtotal
    setDrawColorHex(breakdown?.dividerColor || "#D4AF37");
    doc.setLineWidth(breakdown?.dividerWidth || 1);
    doc.line(leftMargin, y, pageWidth - rightMargin, y);
    y += (breakdown?.subtotal?.marginTop || 6);
    
    // Subtotal
    const subtotalStyle = breakdown?.subtotal || {};
    doc.setFontSize(subtotalStyle.labelFontSize || 11);
    doc.setFont(defaultFont, subtotalStyle.labelFontWeight === "bold" ? "bold" : "normal");
    setTextColorHex(subtotalStyle.labelFontColor || "#000000");
    doc.text(subtotalStyle.labelText || "Subtotal", leftMargin, y);
    
    doc.setFontSize(subtotalStyle.valueFontSize || 11);
    doc.setFont(defaultFont, subtotalStyle.valueFontWeight === "bold" ? "bold" : "normal");
    setTextColorHex(subtotalStyle.valueFontColor || "#000000");
    doc.text(`€${(quoteData?.subtotal || 0).toFixed(2)}`, pageWidth - rightMargin, y, { align: "right" });
    y += (subtotalStyle.marginBottom || 6) + 2;
    
    // City tax row
    checkPageBreak(12);
    const cityTaxLabel = `City Tax (Rome) - ${quoteData?.taxablePeople || 0} persons × ${quoteData?.taxableNights || 0} nights × €${quoteData?.cityTaxRate || 0}`;
    renderPriceRow(cityTaxLabel, `€${(quoteData?.cityTaxTotal || 0).toFixed(2)}`);
    y += (breakdown?.total?.marginTop || 6);
    
    // Divider before total
    checkPageBreak(20);
    setDrawColorHex(breakdown?.dividerColor || "#D4AF37");
    doc.line(leftMargin, y, pageWidth - rightMargin, y);
    y += 8;
    
    // TOTAL
    const totalStyle = breakdown?.total || {};
    doc.setFontSize(totalStyle.labelFontSize || 14);
    doc.setFont(defaultFont, totalStyle.labelFontWeight === "bold" ? "bold" : "normal");
    setTextColorHex(totalStyle.labelFontColor || "#000000");
    doc.text(totalStyle.labelText || "TOTAL", leftMargin, y);
    
    doc.setFontSize(totalStyle.valueFontSize || 14);
    doc.setFont(defaultFont, totalStyle.valueFontWeight === "bold" ? "bold" : "normal");
    setTextColorHex(totalStyle.valueFontColor || "#000000");
    doc.text(`€${(quoteData?.total || 0).toFixed(2)}`, pageWidth - rightMargin, y, { align: "right" });
    y += (totalStyle.marginBottom || 0) + 10;
  }
  
  // ============================================
  // RENDER FINAL NOTES (after TOTAL, before Footer)
  // ============================================
  const finalNotes = templateSettings?.finalNotes;
  // Only render if visible AND at least one field has content
  const hasFinalNotesContent = (finalNotes?.title?.text && finalNotes?.title?.text.trim() !== '') || 
                               (finalNotes?.bodyText?.text && finalNotes?.bodyText?.text.trim() !== '');
  
  if (finalNotes?.visible !== false && hasFinalNotesContent) {
    checkPageBreak(30);
    
    // Add some spacing before final notes
    y += 10;
    
    // Title - WITH WORD WRAP
    if (finalNotes?.title?.visible !== false && finalNotes?.title?.text && finalNotes.title.text.trim() !== '') {
      setFontStyle(finalNotes.title);
      const titleLines = doc.splitTextToSize(finalNotes.title.text, contentWidth);
      titleLines.forEach(line => {
        checkPageBreak(6);
        doc.text(line, getTextX(finalNotes.title.alignment), y, getAlignOption(finalNotes.title.alignment));
        y += 5;
      });
      y += (finalNotes.title.marginBottom || 5);
    }
    
    // Body text - WITH WORD WRAP (supports multi-line with \n)
    if (finalNotes?.bodyText?.visible !== false && finalNotes?.bodyText?.text && finalNotes.bodyText.text.trim() !== '') {
      setFontStyle(finalNotes.bodyText);
      
      // Split by newlines first, then wrap each paragraph
      const paragraphs = finalNotes.bodyText.text.split('\n');
      paragraphs.forEach((paragraph, pIndex) => {
        if (paragraph.trim() === '') {
          // Empty line - add small spacing
          y += 3;
          return;
        }
        
        const lines = doc.splitTextToSize(paragraph, contentWidth);
        lines.forEach((line, lIndex) => {
          checkPageBreak(6);
          doc.text(line, getTextX(finalNotes.bodyText.alignment), y, getAlignOption(finalNotes.bodyText.alignment));
          y += 4.5;
        });
        
        // Add spacing between paragraphs
        if (pIndex < paragraphs.length - 1) {
          y += 2;
        }
      });
      y += (finalNotes.bodyText.marginBottom || 10);
    }
  }
  
  // ============================================
  // RENDER FOOTER WITH LOGO ON ALL PAGES
  // ============================================
  if (logoUrl && footerConfig?.logo?.visible !== false) {
    const totalPages = doc.internal.getNumberOfPages();
    const logoW = footerConfig?.logo?.width || 60;
    const logoH = footerConfig?.logo?.height || 30;
    const logoX = (pageWidth - logoW) / 2; // Centered
    const logoY = pageHeight - logoH - 10; // 10mm from bottom
    
    // Determine image format from data URL
    let imageFormat = 'PNG';
    if (logoUrl.startsWith('data:image/jpeg') || logoUrl.startsWith('data:image/jpg')) {
      imageFormat = 'JPEG';
    } else if (logoUrl.startsWith('data:image/png')) {
      imageFormat = 'PNG';
    } else if (logoUrl.startsWith('data:image/gif')) {
      imageFormat = 'GIF';
    } else if (logoUrl.startsWith('data:image/webp')) {
      imageFormat = 'WEBP';
    }
    
    // Add logo to each page
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      try {
        doc.addImage(logoUrl, imageFormat, logoX, logoY, logoW, logoH);
      } catch (e) {
        console.warn('Could not add logo to PDF:', e);
      }
    }
  }
  
  return doc;
}

// Generate filename for the PDF
export function generateFilename(quoteData) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  const checkIn = new Date(quoteData?.checkIn || new Date());
  const checkOut = new Date(quoteData?.checkOut || new Date());
  
  let dateRange;
  if (checkIn.getFullYear() !== checkOut.getFullYear()) {
    dateRange = `${formatDate(quoteData?.checkIn)} - ${formatDate(quoteData?.checkOut)}`;
  } else if (checkIn.getMonth() !== checkOut.getMonth()) {
    dateRange = `${checkIn.getDate()} ${checkIn.toLocaleDateString("en-GB", { month: "short" })} - ${formatDate(quoteData?.checkOut)}`;
  } else {
    dateRange = `${checkIn.getDate()}-${checkOut.getDate()} ${checkIn.toLocaleDateString("en-GB", { month: "short", year: "numeric" })}`;
  }
  
  let filename = `Suite Lucina - Proposal - ${dateRange}`;
  if (quoteData?.guestName) {
    filename += ` - ${quoteData.guestName}`;
  }
  return filename + ".pdf";
}
